// Account entry at the far right of the TopBar.
//   signed out → "Sign in" (redirects to forge-auth; disabled when no auth
//                URL is configured, e.g. offline/local builds)
//   signed in  → avatar (image or initials) with name/email, Profile,
//                Settings and Sign out
// The Profile view opens as a dialog so the match keeps running underneath.

import { Button } from '@aod/ui/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@aod/ui/components/ui/dropdown-menu';
import { LogIn, LogOut, Settings, UserRound } from 'lucide-react';
import { useState } from 'react';
import type { SessionUser } from '../../auth/session.js';
import { type UseSession, useSession } from '../../auth/useSession.js';
import { useT } from '../../i18n/index.js';
import { appDom } from '../../lib/appDom.js';
import { ProfileDialog } from './ProfileDialog.js';
import { UserAvatar } from './UserAvatar.js';

interface AccountMenuProps {
  readonly onOpenSettings: () => void;
  /** Injected in tests; defaults to the live forge-auth session. */
  readonly session?: UseSession;
}

export function AccountMenu({ onOpenSettings, session }: AccountMenuProps) {
  const live = useSession();
  const { state, configured, signIn, signOut } = session ?? live;
  const t = useT();

  if (state.status === 'signed-in') {
    return <SignedInMenu user={state.user} onOpenSettings={onOpenSettings} onSignOut={signOut} />;
  }

  return (
    <Button
      type="button"
      variant="outline"
      size="sm"
      data-testid="sign-in"
      {...appDom({ id: 'sign-in', type: 'Button', role: 'cta', scope: 'header', label: 'Sign in' })}
      onClick={signIn}
      disabled={!configured || state.status === 'loading'}
      aria-label={t('auth.signIn')}
      title={configured ? t('auth.signIn') : t('auth.unavailable')}
      className="h-8 px-2 sm:px-3"
    >
      <LogIn className="size-4" />
      <span className="hidden sm:inline">{t('auth.signIn')}</span>
    </Button>
  );
}

function SignedInMenu({
  user,
  onOpenSettings,
  onSignOut,
}: {
  readonly user: SessionUser;
  readonly onOpenSettings: () => void;
  readonly onSignOut: () => Promise<void>;
}) {
  const t = useT();
  const [profileOpen, setProfileOpen] = useState(false);
  const displayName = user.name ?? user.email ?? t('profile.unnamed');
  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            data-testid="avatar-menu"
            {...appDom({
              id: 'avatar-menu',
              type: 'Avatar',
              role: 'button',
              scope: 'header',
              label: 'Account menu',
            })}
            aria-label={`${t('auth.account')}: ${displayName}`}
            title={displayName}
            className="size-8 rounded-full p-0"
          >
            <UserAvatar user={user} className="size-7" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="end"
          className="w-60"
          data-testid="avatar-menu-content"
          {...appDom({ id: 'avatar-menu-content', type: 'Dropdown', scope: 'menu' })}
        >
          <DropdownMenuLabel className="flex items-center gap-2.5 py-2 text-sm font-normal normal-case tracking-normal">
            <UserAvatar user={user} className="size-8" />
            <span className="flex min-w-0 flex-col">
              <span className="truncate font-medium text-foreground">{displayName}</span>
              {user.email && (
                <span className="truncate text-xs text-muted-foreground">{user.email}</span>
              )}
            </span>
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            data-testid="menu-profile"
            {...appDom({ id: 'menu-profile', role: 'button', scope: 'menu' })}
            onSelect={() => setProfileOpen(true)}
          >
            <UserRound />
            {t('auth.profile')}
          </DropdownMenuItem>
          <DropdownMenuItem
            data-testid="menu-settings"
            {...appDom({ id: 'menu-settings', role: 'button', scope: 'menu' })}
            onSelect={onOpenSettings}
          >
            <Settings />
            {t('settings.open')}
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            data-testid="menu-sign-out"
            {...appDom({ id: 'menu-sign-out', role: 'button', scope: 'menu' })}
            variant="destructive"
            onSelect={() => void onSignOut()}
          >
            <LogOut />
            {t('auth.signOut')}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      <ProfileDialog
        open={profileOpen}
        onOpenChange={setProfileOpen}
        user={user}
        onSignOut={async () => {
          setProfileOpen(false);
          await onSignOut();
        }}
      />
    </>
  );
}
